import { useEffect, useId, useRef, useState } from 'react';
import type { ResourceDisplay } from '@aztec-realms/shared-types';
import { RewardBundle } from '@aztec-realms/ui';

import './decode-completion-prompt.css';

interface DecodeCompletionPromptProps {
  runId: string;
  ready: boolean;
  verified: boolean;
  pending: boolean;
  online: boolean;
  safetyLocked: boolean;
  weaveOnly?: boolean;
  civicOnly?: boolean;
  personalMaterials?: boolean;
  goalSummary?: string;
  tutorial?: boolean;
  rewards?: readonly ResourceDisplay[];
  error?: string;
  onVerify: () => void;
  onContinue: () => void;
  onViewBoard?: () => void;
  continueLabel: string;
}

/** Presentation only: connected client geometry invites an explicit server replay. */
export function DecodeCompletionPrompt({
  runId,
  ready,
  verified,
  pending,
  online,
  safetyLocked,
  weaveOnly = false,
  civicOnly = false,
  personalMaterials = false,
  goalSummary,
  tutorial = false,
  rewards = [],
  error,
  onVerify,
  onContinue,
  onViewBoard,
  continueLabel,
}: DecodeCompletionPromptProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const submitting = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const [dismissed, setDismissed] = useState<string>();
  const [initiallyCompletedRun] = useState(() => (verified ? runId : undefined));
  const stateKey = `${runId}:${verified ? 'verified' : 'ready'}`;
  const visible =
    (ready || verified) &&
    !(verified && initiallyCompletedRun === runId) &&
    !safetyLocked &&
    dismissed !== stateKey;

  useEffect(() => {
    if (!pending) submitting.current = false;
  }, [pending, error, runId]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !visible) return;
    const previousFocus = document.activeElement;
    dialog.showModal();
    return () => {
      dialog.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus({ preventScroll: true });
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <dialog
      ref={dialogRef}
      className="decode-completion-prompt"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={() => setDismissed(stateKey)}
    >
      <span className="decode-completion-prompt__seal" aria-hidden="true">
        ✦
      </span>
      <h2 id={titleId}>
        {verified
          ? civicOnly
            ? 'A chapter restored'
            : weaveOnly
              ? 'Your connection is ready'
              : 'Glyph restored'
          : 'Your path is connected'}
      </h2>
      <p id={descriptionId}>
        {verified
          ? civicOnly
            ? 'Bring this restoration to your expedition and choose the next path.'
            : weaveOnly
              ? 'Return to Explore to follow your Weave.'
              : (goalSummary ??
                (personalMaterials
                  ? 'Materials recovered for your city.'
                  : 'Knowledge recovered. Choose your next adventure.'))
          : civicOnly
            ? 'Awaken this glyph for your shared expedition.'
            : weaveOnly
              ? 'Awaken your connection. This binding grants no resource rewards.'
              : 'Awaken the glyph to reveal your discovery.'}
      </p>
      {!online && !verified && <p role="status">Your board is safe. Reconnect to continue.</p>}
      {verified && !weaveOnly && !civicOnly && rewards.length > 0 && (
        <RewardBundle
          title={personalMaterials ? 'Materials recovered' : 'Knowledge recovered'}
          resources={rewards}
        />
      )}
      {error && !verified && (
        <p className="decode-completion-prompt__error" role="alert">
          The glyph could not awaken. Try again when you are ready.
        </p>
      )}
      <div className="decode-completion-prompt__actions">
        {verified ? (
          <button type="button" className="decode-completion-prompt__primary" onClick={onContinue}>
            {continueLabel}
          </button>
        ) : (
          <button
            type="button"
            aria-label={tutorial ? 'Verify and awaken glyph' : undefined}
            className="decode-completion-prompt__primary"
            disabled={!online || pending}
            onClick={() => {
              if (submitting.current || !online || pending) return;
              submitting.current = true;
              onVerify();
            }}
          >
            {pending ? 'Awakening…' : weaveOnly ? 'Awaken connection' : 'Awaken glyph'}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setDismissed(stateKey);
            onViewBoard?.();
          }}
        >
          {verified ? 'View restored glyph' : 'Keep viewing the board'}
        </button>
      </div>
    </dialog>
  );
}
